import React, { useState, useEffect, useMemo } from 'react';
import {
  EnvelopeIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  EyeIcon,
  CodeBracketIcon,
  ChevronDownIcon,
  UserGroupIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import AdminShell from '../../components/layout/AdminShell';
import { adminNavigation } from '../../constants/navigation';
import { api } from '../../lib/supabase';
import Toast, { ToastType } from '../../components/ui/Toast';

interface EmailTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  subject: string;
  html_body: string;
  available_placeholders: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Templates sent directly to LOs/Recruits/Prospects
const EXTERNAL_TEMPLATE_SLUGS = [
  'lo-onboarding-welcome',
  'portal-welcome-magic-link',
  'prospect-onboarding',
  'recruit-welcome',
];

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Folder collapse state
  const [externalExpanded, setExternalExpanded] = useState(true);
  const [internalExpanded, setInternalExpanded] = useState(true);

  // Edit form state
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');

  // Categorize templates
  const { externalTemplates, internalTemplates } = useMemo(() => {
    const external = templates.filter(t => EXTERNAL_TEMPLATE_SLUGS.includes(t.slug));
    const internal = templates.filter(t => !EXTERNAL_TEMPLATE_SLUGS.includes(t.slug));
    return { externalTemplates: external, internalTemplates: internal };
  }, [templates]);

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await api.getEmailTemplates();
      setTemplates(data || []);
      // Select first template by default
      if (data && data.length > 0 && !selectedTemplate) {
        setSelectedTemplate(data[0]);
        setEditSubject(data[0].subject);
        setEditBody(data[0].html_body);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setToast({ message: 'Failed to load email templates', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Select a template
  const handleSelectTemplate = (template: EmailTemplate) => {
    if (isEditing) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedTemplate(template);
    setEditSubject(template.subject);
    setEditBody(template.html_body);
    setIsEditing(false);
    setShowPreview(false);
  };

  // Start editing
  const handleStartEdit = () => {
    setIsEditing(true);
    setShowPreview(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    if (selectedTemplate) {
      setEditSubject(selectedTemplate.subject);
      setEditBody(selectedTemplate.html_body);
    }
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      await api.updateEmailTemplate(selectedTemplate.id, {
        subject: editSubject,
        html_body: editBody,
      });

      // Update local state
      setTemplates(templates.map(t =>
        t.id === selectedTemplate.id
          ? { ...t, subject: editSubject, html_body: editBody }
          : t
      ));
      setSelectedTemplate({ ...selectedTemplate, subject: editSubject, html_body: editBody });
      setIsEditing(false);
      setToast({ message: 'Email template saved successfully', type: 'success' });
    } catch (err) {
      console.error('Error saving template:', err);
      setToast({ message: 'Failed to save template', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Replace placeholders with sample values for preview
  const getPreviewHtml = () => {
    let html = editBody;
    const sampleValues: Record<string, string> = {
      first_name: 'John',
      last_name: 'Doe',
      email: 'john.doe@example.com',
      nmls: '123456',
      onboarding_url: 'https://onboarding.luminlending.com/onboarding-form?token=sample-token',
    };

    for (const [key, value] of Object.entries(sampleValues)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      html = html.replace(regex, value);
    }

    return html;
  };

  if (loading) {
    return (
      <AdminShell
        title="Email Templates"
        description="Manage email templates sent to recruits and prospects"
        nav={adminNavigation}
      >
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-lumin-primary/30 border-t-lumin-primary rounded-full animate-spin"></div>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title="Email Templates"
      description="Manage email templates sent to recruits and prospects"
      nav={adminNavigation}
    >
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template List - Left sidebar */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <EnvelopeIcon className="h-4 w-4" />
              Templates
            </h3>
            <div className="space-y-3">
              {/* External Emails Folder */}
              <div>
                <button
                  onClick={() => setExternalExpanded(!externalExpanded)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <UserGroupIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">External Emails</span>
                    <span className="text-xs text-slate-400">({externalTemplates.length})</span>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${externalExpanded ? '' : '-rotate-90'}`} />
                </button>
                {externalExpanded && (
                  <div className="mt-1 ml-2 space-y-1 border-l-2 border-blue-100 pl-2">
                    {externalTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedTemplate?.id === template.id
                            ? 'bg-lumin-primary text-white'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <p className="font-medium text-sm truncate">{template.name}</p>
                        <p className={`text-xs truncate ${
                          selectedTemplate?.id === template.id ? 'text-white/70' : 'text-slate-500'
                        }`}>
                          {template.slug}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Internal Notifications Folder */}
              <div>
                <button
                  onClick={() => setInternalExpanded(!internalExpanded)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BellIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-semibold text-slate-700">Internal Notifications</span>
                    <span className="text-xs text-slate-400">({internalTemplates.length})</span>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${internalExpanded ? '' : '-rotate-90'}`} />
                </button>
                {internalExpanded && (
                  <div className="mt-1 ml-2 space-y-1 border-l-2 border-amber-100 pl-2">
                    {internalTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedTemplate?.id === template.id
                            ? 'bg-lumin-primary text-white'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <p className="font-medium text-sm truncate">{template.name}</p>
                        <p className={`text-xs truncate ${
                          selectedTemplate?.id === template.id ? 'text-white/70' : 'text-slate-500'
                        }`}>
                          {template.slug}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Template Editor - Main area */}
        <div className="lg:col-span-3">
          {selectedTemplate ? (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
              {/* Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{selectedTemplate.name}</h2>
                    {selectedTemplate.description && (
                      <p className="text-sm text-slate-500 mt-1">{selectedTemplate.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 flex items-center gap-1"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {saving ? (
                            <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <CheckIcon className="h-4 w-4" />
                          )}
                          Save Changes
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg flex items-center gap-1 ${
                            showPreview
                              ? 'bg-lumin-primary text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          {showPreview ? <CodeBracketIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                          {showPreview ? 'View Code' : 'Preview'}
                        </button>
                        <button
                          onClick={handleStartEdit}
                          className="px-4 py-2 text-sm font-medium bg-lumin-primary text-white rounded-lg hover:bg-[#014f70] flex items-center gap-1"
                        >
                          <PencilIcon className="h-4 w-4" />
                          Edit Template
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Available placeholders */}
                {selectedTemplate.available_placeholders && selectedTemplate.available_placeholders.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">Available placeholders:</span>
                    {selectedTemplate.available_placeholders.map((placeholder) => (
                      <code
                        key={placeholder}
                        className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded font-mono"
                      >
                        {`{{${placeholder}}}`}
                      </code>
                    ))}
                  </div>
                )}
              </div>

              {/* Subject */}
              <div className="p-6 border-b border-slate-100">
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject Line</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-lumin-primary font-mono text-sm"
                  />
                ) : (
                  <p className="text-slate-900 font-mono text-sm bg-slate-50 px-4 py-2 rounded-lg">
                    {selectedTemplate.subject}
                  </p>
                )}
              </div>

              {/* Body */}
              <div className="p-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Body</label>
                {showPreview && !isEditing ? (
                  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                    <iframe
                      srcDoc={getPreviewHtml()}
                      title="Email Preview"
                      className="w-full h-[600px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                ) : isEditing ? (
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={25}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-lumin-primary font-mono text-xs leading-relaxed"
                    spellCheck={false}
                  />
                ) : (
                  <pre className="text-xs text-slate-700 bg-slate-50 p-4 rounded-lg overflow-auto max-h-[600px] font-mono whitespace-pre-wrap">
                    {selectedTemplate.html_body}
                  </pre>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-white p-12 shadow-sm text-center">
              <EnvelopeIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Select a template to view and edit</p>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default EmailTemplates;
